/**
 * ModifierGroup — editable list of {key, label, multiplier} rows for a rate
 * card's region / sector / size modifiers.
 */
import React from "react";
import { Plus, Trash2 } from "lucide-react";
import InfoTooltip from "../InfoTooltip";
import NumInput from "./NumInput";

export default function ModifierGroup({ title, items, onChange, testidPrefix, tooltip }) {
  const update = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const add = () => {
    onChange([...items, { key: `key_${Date.now()}`, label: "New modifier", multiplier: 1.0 }]);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="border border-zinc-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-zinc-50">
        <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-zinc-700">{title}{tooltip ? <InfoTooltip testid={`${testidPrefix}-info`}>{tooltip}</InfoTooltip> : null}</div>
        <button
          type="button"
          onClick={add}
          className="text-[11px] text-[#7737F6] hover:text-[#5D28C7] inline-flex items-center gap-1"
          data-testid={`${testidPrefix}-add`}
        >
          <Plus size={11}/> add
        </button>
      </div>
      <div className="p-3 space-y-1.5">
        {items.length === 0 && <div className="text-[12px] text-zinc-400 italic">No modifiers configured.</div>}
        {items.map((m, i) => (
          <div key={i} className="grid grid-cols-[1fr_1.5fr_auto_auto] gap-2 items-center">
            <input
              value={m.key}
              onChange={(e) => update(i, { key: e.target.value })}
              placeholder="key"
              className="border border-zinc-300 px-2 py-1 text-[12px] font-mono focus:outline-none focus:border-[#7737F6]"
              data-testid={`${testidPrefix}-key-${i}`}
            />
            <input
              value={m.label}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="label"
              className="border border-zinc-300 px-2 py-1 text-[12.5px] focus:outline-none focus:border-[#7737F6]"
              data-testid={`${testidPrefix}-label-${i}`}
            />
            <NumInput
              value={m.multiplier}
              onChange={(v) => update(i, { multiplier: v })}
              suffix="×"
              min={0.5}
              max={2.0}
              step={0.05}
              testid={`${testidPrefix}-mult-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-zinc-400 hover:text-red-600 p-1"
              data-testid={`${testidPrefix}-remove-${i}`}
              aria-label="remove"
            >
              <Trash2 size={12}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
